import { useState, useEffect, useCallback } from 'react';
import { usePharma } from '../../../providers/PharmaProvider';
import { LayoutCountData } from '../interfaces/participantTrackerTypes';
import type { SptDataSource } from '../types';

const API_URL = 'https://dev.seevividly.com/api_clinical_trials/get_layout_counts';

export const useLayoutCounts = (sptSource: SptDataSource = 'production') => {
  const { user } = usePharma();
  const [data, setData] = useState<LayoutCountData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.user_name || !user?.p) {
      setData([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          u: user.user_name,
          p: user.p,
          ...(sptSource !== 'production' && { spt_source: sptSource }),
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error("Layout Counts API Error Response:");
        throw new Error(`Layout Counts API request failed: ${response.status} ${response.statusText || ''}.`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError: any) {
        console.error("Failed to parse Layout Counts API response:");
        throw new Error(`Invalid JSON received from Layout Counts API. ${parseError.message}`);
      }

      if (result?.status === 'success' && result.data && Array.isArray(result.data.data)) {
        const processedData = (result.data.data as any[]).map(item => ({
          ...item,
          layouts_json: Array.isArray(item.layouts_json) ? item.layouts_json : (Array.isArray(item.layouts) ? item.layouts : []) 
          // Handle both layouts_json and layouts for flexibility from API
        }));
        setData(processedData as LayoutCountData[]);
      } else {
        console.error('Unexpected Layout Counts API response format:');
        setError('Received invalid data format from Layout Counts API.');
        setData([]);
      }
    } catch (err: any) {
      console.error('Fetch layout counts error:');
      setError(err.message || 'Failed to load layout count data.');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, sptSource]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};
